import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError, get } from "@/lib/api";
import { KpiTile, PageHeader, Section } from "@/components/common/Layout";
import { DataTable } from "@/components/common/DataTable";
import { StatusBadge } from "@/components/common/StatusBadge";
import { SyncBadge, ModeBadge } from "@/components/common/SyncBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { dt, money, title } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";

const errText = (detail) => {
  if (!detail) return "";
  if (typeof detail === "string") return detail;
  return detail.reason || detail.message || detail.url || detail.action || JSON.stringify(detail);
};

const PROVIDER_LABELS = { illyvoip: "IllyVoIP", bitcall: "Bitcall", mmdsmart: "MMDSmart", voiso: "Voiso", vapi: "Vapi", elevenlabs: "ElevenLabs", twilio: "Twilio", telnyx: "Telnyx", openai: "OpenAI", retell: "Retell AI", s3: "S3" };
const providerLabel = (key) => PROVIDER_LABELS[key] || title(key);

const SECRET_FIELD = (f) => /key|password|token|secret/i.test(f);
const FIELD_OPTIONS = { region: ["de", "us", "sg", "eu"], transport: ["udp", "tcp", "tls"], sandbox: ["false", "true"] };

const FIELD_LABELS = { sip_domain: "SIP domain", sip_port: "SIP port", sip_gateway: "SIP gateway", sip_username: "SIP username", sip_password: "SIP password", sip_user_id: "SIP user ID", sip_auth_username: "SIP auth username", sip_auth_password: "SIP auth password", caller_id: "Caller ID", api_key: "API key", sandbox: "Sandbox mode", cluster_id: "Cluster ID", account_sid: "Account SID", auth_token: "Auth token", voice_api_base_url: "Voice API base URL (human click-to-call)", voice_api_key: "Voice API key (human click-to-call)", voice_trunk_id: "Voice trunk ID (optional)", voice_click_to_call_path: "Click-to-call path (optional)" };

const SIP_TRUNK_PROVIDERS = ["voiso", "illyvoip", "bitcall", "mmdsmart"];

const DEFAULT_KEYS = [
  ["voice_agent_provider", "Voice agent provider", ["vapi", "retell"]],
  ["telephony_provider", "Telephony provider", ["mmdsmart", "voiso", "illyvoip", "bitcall", "twilio", "telnyx"]],
  ["llm_provider", "LLM provider", ["vapi", "openai"]],
  ["voice_provider", "Voice provider", ["vapi", "elevenlabs"]],
  ["storage_provider", "Storage provider", ["local", "s3"]],
  ["fallback_voice_agent_provider", "Fallback voice agent", ["", "vapi"]],
  ["fallback_telephony_provider", "Fallback telephony", ["", "mmdsmart", "voiso", "illyvoip", "bitcall", "twilio"]],
];

export default function ProviderCenter() {
  const { user } = useAuth();
  const isAdmin = user?.role === "super_admin";
  const queryClient = useQueryClient();
  const [configuring, setConfiguring] = useState(null);
  const [values, setValues] = useState({});
  const [testResult, setTestResult] = useState(null);
  const [defaults, setDefaults] = useState(null);
  const [search, setSearch] = useState({ country: "US", number_type: "local", area_code: "" });
  const [searchResult, setSearchResult] = useState(null);
  const [secret, setSecret] = useState(null);

  const { data } = useQuery({ queryKey: ["pc-overview"], queryFn: () => get("/provider-center/overview"), refetchInterval: 20000 });
  const { data: readiness } = useQuery({ queryKey: ["pc-readiness"], queryFn: () => get("/provider-center/live-readiness"), refetchInterval: 20000 });
  const { data: webhooks } = useQuery({ queryKey: ["pc-webhooks"], queryFn: () => get("/provider-center/webhooks") });
  const { data: flags } = useQuery({ queryKey: ["pc-flags"], queryFn: () => get("/provider-center/flags") });
  const { data: providerNumbers } = useQuery({ queryKey: ["pc-numbers"], queryFn: () => get("/phone-numbers/provider/list").catch(() => ({ items: [] })) });

  const invalidateAll = () => queryClient.invalidateQueries();

  const testCreds = useMutation({
    mutationFn: () => api.post("/provider-center/credentials/test", { provider: configuring.key, values }),
    onSuccess: ({ data: r }) => { setTestResult({ ok: true, ...r }); toast.success("Credentials are valid"); },
    onError: (e) => { setTestResult({ ok: false, error: errText(e.response?.data?.detail) }); toast.error(errText(e.response?.data?.detail) || apiError(e)); },
  });

  const saveCreds = useMutation({
    mutationFn: () => api.post("/provider-center/credentials", { provider: configuring.key, label: configuring.name, values }),
    onSuccess: () => { toast.success("Credentials validated, encrypted and stored"); setConfiguring(null); setValues({}); setTestResult(null); invalidateAll(); },
    onError: (e) => toast.error(errText(e.response?.data?.detail?.provider_detail) || errText(e.response?.data?.detail) || apiError(e)),
  });

  const health = useMutation({
    mutationFn: (key) => api.post(`/provider-center/credentials/${key}/health`),
    onSuccess: ({ data: r }) => { toast.success(`${providerLabel(r.provider)} connection healthy${r.balance != null ? ` · balance ${money(r.balance)}` : ""}`); invalidateAll(); },
    onError: (e) => toast.error(errText(e.response?.data?.detail) || apiError(e)),
  });

  const disable = useMutation({
    mutationFn: (key) => api.post(`/provider-center/credentials/${key}/disable`),
    onSuccess: () => { toast.success("Provider disabled"); invalidateAll(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const linkTrunk = useMutation({
    mutationFn: (key) => api.post(`/provider-center/${key}/link-vapi`),
    onSuccess: ({ data }) => {
      toast.success(`SIP trunk linked · ${data.sip_host} (${data.region})`, { duration: 9000 });
      toast.info(data.note, { duration: 12000 });
      invalidateAll();
    },
    onError: (e) => toast.error(
      e.response?.data?.detail?.message || e.response?.data?.detail?.reason || apiError(e)),
  });

  const saveDefaults = useMutation({
    mutationFn: () => api.put("/provider-center/defaults", defaults),
    onSuccess: () => { toast.success("Provider defaults saved"); setDefaults(null); invalidateAll(); },
    onError: (e) => toast.error(errText(e.response?.data?.detail) || apiError(e)),
  });

  const rotate = useMutation({
    mutationFn: (provider) => api.post(`/provider-center/webhooks/${provider}/rotate-secret`),
    onSuccess: ({ data: r }) => { setSecret(r); invalidateAll(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const testHook = useMutation({
    mutationFn: (provider) => api.post(`/provider-center/webhooks/${provider}/test`),
    onSuccess: () => { toast.success("Test event delivered through the normalization pipeline"); invalidateAll(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const setFlag = useMutation({
    mutationFn: ({ key, value }) => api.put("/provider-center/flags", { flags: { [key]: value } }),
    onSuccess: () => { toast.success("Feature flag updated"); invalidateAll(); },
    onError: (e) => toast.error(apiError(e)),
  });

  const runSearch = useMutation({
    mutationFn: () => get("/phone-numbers/provider/search", { ...search, area_code: search.area_code || undefined }),
    onSuccess: (r) => setSearchResult(r),
    onError: (e) => toast.error(errText(e.response?.data?.detail) || apiError(e)),
  });

  const importNumber = useMutation({
    mutationFn: (phone) => api.post("/phone-numbers/provider/import", { phone }),
    onSuccess: () => { toast.success("Number imported and synchronised"); invalidateAll(); },
    onError: (e) => toast.error(errText(e.response?.data?.detail) || apiError(e)),
  });

  const providers = data?.providers || [];
  const connected = providers.filter((p) => p.connected).length;

  return (
    <>
      <PageHeader
        testId="provider-center-page"
        title="Provider Center"
        subtitle="Connect providers once, then run every voice and telephony operation from this panel"
        actions={<ModeBadge mode="live" testId="pc-mode-badge" />}
      />

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <KpiTile testId="pc-kpi-connected" label="Connected providers" value={connected} tone="success" />
        <KpiTile testId="pc-kpi-resources" label="Synced resources" value={providers.reduce((a, p) => a + p.synced_resources, 0)} />
        <KpiTile testId="pc-kpi-errors" label="Open provider errors" value={providers.reduce((a, p) => a + p.error_count, 0)} tone="danger" />
        <KpiTile testId="pc-kpi-active" label="Active provider calls" value={providers.reduce((a, p) => a + p.active_calls, 0)} tone="primary" />
      </div>

      <Tabs defaultValue="overview">
        <TabsList className="max-w-full flex-nowrap justify-start overflow-x-auto bg-slate-100">
          <TabsTrigger value="overview" data-testid="pc-tab-overview">Providers</TabsTrigger>
          <TabsTrigger value="capabilities" data-testid="pc-tab-capabilities">Capabilities & defaults</TabsTrigger>
          <TabsTrigger value="numbers" data-testid="pc-tab-numbers">Provider numbers</TabsTrigger>
          <TabsTrigger value="webhooks" data-testid="pc-tab-webhooks">Webhooks</TabsTrigger>
          <TabsTrigger value="flags" data-testid="pc-tab-flags">Feature flags</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <Section
            testId="pc-live-readiness"
            title="Live mode readiness"
            subtitle={`Voice agent: ${readiness?.voice_agent_provider || "—"} · Telephony: ${readiness?.telephony_provider || "—"}`}
            actions={
              <StatusBadge
                value={readiness?.ready ? "completed" : "paused"}
                label={readiness?.ready ? "Ready for live calls" : `${readiness?.blockers ?? 0} blocker(s)`}
                testId="pc-readiness-badge"
              />
            }
          >
            <div className="grid grid-cols-1 gap-2 p-4 md:grid-cols-2">
              {(readiness?.checks || []).map((check) => (
                <div key={check.id} className="flex items-start gap-2 rounded-md border border-slate-200 px-3 py-2" data-testid={`readiness-${check.id}`}>
                  <span className={`mt-0.5 h-2 w-2 shrink-0 rounded-full ${check.ok ? "bg-emerald-500" : check.blocking ? "bg-rose-500" : "bg-amber-400"}`} />
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-slate-800">{check.label}</p>
                    {!check.ok && check.action ? (
                      <p className="text-[11px] text-slate-500">{check.action}</p>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {providers.map((p) => (
            <Section
              key={p.key}
              testId={`provider-card-${p.key}`}
              title={p.name}
              subtitle={p.description}
              actions={<div className="flex gap-1"><ModeBadge mode={p.mode} /><StatusBadge value={p.connected ? "completed" : "draft"} label={p.connected ? "Connected" : "Not connected"} /></div>}
            >
              <dl className="grid grid-cols-2 gap-2 p-4 text-sm">
                {[
                  ["Credential health", title(p.credential_health || "unknown")],
                  ["Key", p.credential_preview || "—"],
                  ["Balance", p.balance != null ? money(p.balance) : "Not exposed"],
                  ["Account status", title(p.account_status || "—")],
                  ["Last API success", p.last_successful_request_at ? dt(p.last_successful_request_at) : "—"],
                  ["Last webhook", p.last_webhook_at ? dt(p.last_webhook_at) : "—"],
                  ["Synced resources", p.synced_resources],
                  ["Active calls", p.active_calls],
                  ["Errors", p.error_count],
                  ["Changed by", p.credential_updated_by || "—"],
                ].map(([k, v]) => (
                  <div key={k}><dt className="kpi-label">{k}</dt><dd className="truncate font-medium text-slate-800">{v}</dd></div>
                ))}
              </dl>
              {p.external_actions?.length ? (
                <p className="border-t border-slate-200 px-4 py-2 text-xs text-amber-700">
                  External provider actions: {p.external_actions.join(" · ")}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 border-t border-slate-200 px-4 py-3">
                <Button size="sm" variant="outline" onClick={() => health.mutate(p.key)} data-testid={`pc-test-${p.key}`}>Test connection</Button>
                {isAdmin && p.credential_fields?.length ? (
                  <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => { setConfiguring(p); setValues({}); setTestResult(null); }} data-testid={`pc-configure-${p.key}`}>
                    {p.connected && p.credential_preview ? "Rotate credentials" : "Configure"}
                  </Button>
                ) : null}
                {isAdmin && p.credential_preview && !p.disabled ? (
                  <Button size="sm" variant="outline" className="text-rose-600" onClick={() => disable.mutate(p.key)} data-testid={`pc-disable-${p.key}`}>Disable</Button>
                ) : null}
                {isAdmin && SIP_TRUNK_PROVIDERS.includes(p.key) ? (
                  <Button size="sm" variant="outline" className="text-indigo-600" disabled={linkTrunk.isPending} onClick={() => linkTrunk.mutate(p.key)} data-testid={`pc-link-sip-trunk-${p.key}`}>
                    {linkTrunk.isPending ? "Linking…" : "Link SIP trunk to Vapi"}
                  </Button>
                ) : null}
              </div>
            </Section>
          ))}
          </div>
        </TabsContent>

        <TabsContent value="capabilities" className="mt-4 space-y-4">
          <Section title="Capability matrix" testId="pc-capability-matrix">
            <DataTable
              testId="pc-capability-table"
              columns={[
                { key: "capability", label: "Capability", render: (r) => <span className="font-medium text-slate-800">{title(r.capability)}</span> },
                { key: "providers", label: "Providers", render: (r) => (
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(r.map).map(([provider, supported]) => (
                      <StatusBadge key={provider} value={supported ? "completed" : "draft"} label={`${providerLabel(provider)}${supported ? "" : " (not supported)"}`} />
                    ))}
                  </div>
                ) },
              ]}
              rows={Object.entries(data?.capability_matrix || {}).map(([capability, map]) => ({ id: capability, capability, map }))}
              empty={{ title: "No capability data" }}
            />
          </Section>

          <Section title="Provider defaults" subtitle="Incompatible combinations are rejected by the backend" testId="pc-defaults">
            <div className="grid grid-cols-1 gap-4 p-4 md:grid-cols-3">
              {DEFAULT_KEYS.map(([key, label, options]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Select
                    value={(defaults?.[key] ?? data?.defaults?.[key]) || "none"}
                    disabled={!isAdmin}
                    onValueChange={(v) => setDefaults((d) => ({ ...(d || data.defaults), [key]: v === "none" ? null : v }))}
                  >
                    <SelectTrigger data-testid={`pc-default-${key}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {options.map((o) => <SelectItem key={o || "none"} value={o || "none"}>{o ? providerLabel(o) : "None"}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
              {isAdmin ? (
                <div className="flex items-end">
                  <Button className="bg-blue-600 hover:bg-blue-700" disabled={!defaults || saveDefaults.isPending} onClick={() => saveDefaults.mutate()} data-testid="pc-defaults-save">Save defaults</Button>
                </div>
              ) : null}
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="numbers" className="mt-4 space-y-4">
          <Section title="Numbers owned in the provider account" testId="pc-provider-numbers">
            <DataTable
              testId="pc-provider-numbers-table"
              columns={[
                { key: "phone", label: "Number", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
                { key: "label", label: "Label" },
                { key: "provider_id", label: "Provider SID", render: (r) => <span className="font-mono text-[11px]">{r.provider_id}</span> },
                { key: "imported", label: "In ai2Dial", render: (r) => <StatusBadge value={r.imported ? "completed" : "draft"} label={r.imported ? "Imported" : "Not imported"} /> },
                { key: "actions", label: "", render: (r) => (!r.imported && isAdmin ? (
                  <Button size="sm" variant="outline" onClick={() => importNumber.mutate(r.phone)} data-testid={`pc-import-${r.phone}`}>Import & sync</Button>
                ) : null) },
              ]}
              rows={(providerNumbers?.items || []).map((n) => ({ ...n, id: n.provider_id }))}
              empty={{ title: "No provider numbers", description: "Connect a telephony provider to list the numbers it owns." }}
            />
          </Section>

          <Section title="Search available numbers" testId="pc-number-search">
            <div className="flex flex-wrap items-end gap-3 p-4">
              <div className="space-y-1.5"><Label>Country</Label><Input data-testid="pc-search-country" className="w-24" value={search.country} onChange={(e) => setSearch((s) => ({ ...s, country: e.target.value.toUpperCase() }))} /></div>
              <div className="space-y-1.5">
                <Label>Type</Label>
                <Select value={search.number_type} onValueChange={(v) => setSearch((s) => ({ ...s, number_type: v }))}>
                  <SelectTrigger data-testid="pc-search-type" className="w-36"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="local">Local</SelectItem>
                    <SelectItem value="toll_free">Toll free</SelectItem>
                    <SelectItem value="mobile">Mobile</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Area code</Label><Input data-testid="pc-search-area" className="w-28" value={search.area_code} onChange={(e) => setSearch((s) => ({ ...s, area_code: e.target.value }))} /></div>
              <Button variant="outline" onClick={() => runSearch.mutate()} data-testid="pc-search-run">Search</Button>
            </div>
            {searchResult ? (
              <>
                {searchResult.note ? <p className="px-4 pb-2 text-xs text-amber-700" data-testid="pc-purchase-note">{searchResult.note}</p> : null}
                <DataTable
                  testId="pc-search-results"
                  columns={[
                    { key: "phone", label: "Number", render: (r) => <span className="font-mono text-xs">{r.phone}</span> },
                    { key: "locality", label: "Locality", render: (r) => r.locality || "—" },
                    { key: "region", label: "Region", render: (r) => r.region || "—" },
                    { key: "type", label: "Type", render: (r) => title(r.type) },
                    { key: "setup_price", label: "Setup", render: (r) => (r.setup_price != null ? money(r.setup_price) : "Provider pricing") },
                    { key: "monthly_price", label: "Monthly", render: (r) => (r.monthly_price != null ? money(r.monthly_price) : "Provider pricing") },
                    { key: "address_required", label: "Documents", render: (r) => (r.address_required && r.address_required !== "none" ? <StatusBadge value="paused" label={`Regulatory: ${r.address_required}`} /> : "None") },
                    { key: "actions", label: "", render: () => (
                      <Button size="sm" variant="outline" disabled title="Purchasing is disabled by feature flag" data-testid="pc-purchase-disabled">Purchase disabled</Button>
                    ) },
                  ]}
                  rows={(searchResult.items || []).map((n, i) => ({ ...n, id: n.phone || i }))}
                  empty={{ title: "No numbers matched" }}
                />
              </>
            ) : null}
          </Section>
        </TabsContent>

        <TabsContent value="webhooks" className="mt-4">
          <Section title="Webhook endpoints" subtitle={`Public base URL: ${webhooks?.public_base_url || "not configured"}`} testId="pc-webhooks">
            <DataTable
              testId="pc-webhooks-table"
              columns={[
                { key: "name", label: "Provider" },
                { key: "url", label: "Webhook URL", render: (r) => <span className="font-mono text-[11px]">{r.url || "—"}</span> },
                { key: "registered", label: "Secret", render: (r) => <StatusBadge value={r.registered ? "completed" : "draft"} label={r.registered ? `Configured ${r.secret_preview || ""}` : "Not configured"} /> },
                { key: "registration_mode", label: "Registration", render: (r) => title(r.registration_mode) },
                { key: "last_event_at", label: "Last event", render: (r) => (r.last_event_at ? `${r.last_event} · ${dt(r.last_event_at)}` : "—") },
                { key: "unprocessed_events", label: "Unprocessed" },
                { key: "actions", label: "", render: (r) => (isAdmin ? (
                  <div className="flex gap-1">
                    <Button size="sm" variant="ghost" onClick={() => rotate.mutate(r.provider)} data-testid={`pc-rotate-${r.provider}`}>Rotate secret</Button>
                    <Button size="sm" variant="ghost" onClick={() => testHook.mutate(r.provider)} data-testid={`pc-testhook-${r.provider}`}>Test</Button>
                  </div>
                ) : null) },
              ]}
              rows={(webhooks?.webhooks || []).map((w) => ({ ...w, id: w.provider }))}
              empty={{ title: "No webhook providers" }}
            />
          </Section>
        </TabsContent>

        <TabsContent value="flags" className="mt-4">
          <Section title="Feature flags" testId="pc-flags">
            <div className="divide-y divide-slate-100">
              {Object.entries(flags || {}).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-800">{title(key)}</p>
                    <p className="text-xs text-slate-500">
                      {key === "allow_number_purchase"
                        ? "Buying numbers spends real provider money; keep disabled unless required."
                        : key === "allow_live_mode"
                          ? "When disabled, campaigns cannot be launched at all."
                          : "Backend-enforced operational flag."}
                    </p>
                  </div>
                  <Switch data-testid={`pc-flag-${key}`} checked={!!value} disabled={!isAdmin} onCheckedChange={(v) => setFlag.mutate({ key, value: v })} />
                </div>
              ))}
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      <Dialog open={!!configuring} onOpenChange={(o) => !o && setConfiguring(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Connect {configuring?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {(configuring?.credential_fields || []).map((field) => (
              <div key={field} className="space-y-1.5">
                <Label>{FIELD_LABELS[field] || title(field)}</Label>
                {FIELD_OPTIONS[field] ? (
                  <Select value={values[field] || ""} onValueChange={(v) => setValues((o) => ({ ...o, [field]: v }))}>
                    <SelectTrigger data-testid={`pc-field-${field}`}><SelectValue placeholder="Select" /></SelectTrigger>
                    <SelectContent className="bg-white">
                      {FIELD_OPTIONS[field].map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input data-testid={`pc-field-${field}`} type={SECRET_FIELD(field) ? "password" : "text"} value={values[field] || ""} onChange={(e) => setValues((v) => ({ ...v, [field]: e.target.value }))} />
                )}
              </div>
            ))}
            <p className="text-xs text-slate-500">
              Credentials are validated against the provider, then encrypted. Only a masked preview is
              ever returned to this panel.
            </p>
            {testResult ? (
              <p data-testid="pc-test-result" className={`rounded-md px-3 py-2 text-sm ${testResult.ok ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
                {testResult.ok
                  ? `Valid${testResult.balance != null ? ` · balance ${money(testResult.balance)}` : ""}${testResult.account?.name ? ` · ${testResult.account.name}` : ""}`
                  : testResult.error}
              </p>
            ) : null}
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={testCreds.isPending} onClick={() => testCreds.mutate()} data-testid="pc-test-creds">Test credentials</Button>
            <Button className="bg-blue-600 hover:bg-blue-700" disabled={saveCreds.isPending} onClick={() => saveCreds.mutate()} data-testid="pc-save-creds">Validate & save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!secret} onOpenChange={(o) => !o && setSecret(null)}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>Webhook secret for {secret?.provider}</DialogTitle></DialogHeader>
          <p className="break-all rounded-md bg-slate-50 px-3 py-2 font-mono text-xs" data-testid="pc-new-secret">{secret?.secret}</p>
          <p className="text-xs text-slate-500">{secret?.note}</p>
          <p className="break-all text-xs text-slate-600">URL: {secret?.url}</p>
        </DialogContent>
      </Dialog>
    </>
  );
}